import {
    SdsLocale,
    type InternalSdsBuildRequest,
} from "../../data/models/schemas";

const LOCALE_LANGUAGE_TAGS: Record<SdsLocale, string> = {
    [SdsLocale.EN_US]: "en-US",
    [SdsLocale.EN_CA]: "en-CA",
    [SdsLocale.FR_CA]: "fr-CA",
    [SdsLocale.DE_DE]: "de-DE",
    [SdsLocale.EN_AU]: "en-AU",
    [SdsLocale.JA_JP]: "ja-JP",
};

const compactObject = <Value extends object>(value: Value): Value =>
    Object.fromEntries(
        Object.entries(value).filter(([, child]) => {
            if (child === undefined || child === null) return false;
            return typeof child !== "string" || child.trim().length > 0;
        })
    ) as Value;

/**
 * Removes empty optional values while preserving the generated internal request
 * type. This is request cleanup only; generated SDS responses are consumed
 * directly without a public-response adapter.
 */
export const sanitizeSdsBuildRequest = (
    request: InternalSdsBuildRequest
): InternalSdsBuildRequest => {
    const { product, supplier, emergency_contact, ...required } = request;
    const compactProduct = product ? compactObject(product) : undefined;
    const compactSupplier = supplier ? compactObject(supplier) : undefined;
    const compactEmergencyContact = emergency_contact
        ? compactObject(emergency_contact)
        : undefined;

    return {
        ...required,
        ingredients: request.ingredients.map((ingredient) =>
            compactObject({ ...ingredient })
        ),
        ...(compactProduct && Object.keys(compactProduct).length
            ? { product: compactProduct }
            : {}),
        ...(compactSupplier && Object.keys(compactSupplier).length
            ? { supplier: compactSupplier }
            : {}),
        ...(compactEmergencyContact &&
        Object.keys(compactEmergencyContact).length
            ? { emergency_contact: compactEmergencyContact }
            : {}),
    };
};

export const getSdsLocaleLanguageTag = (locale: SdsLocale): string =>
    LOCALE_LANGUAGE_TAGS[locale];

export const getSdsLocaleFromLanguageTag = (
    languageTag?: string | null
): SdsLocale | undefined => {
    const normalized = languageTag?.toLowerCase();
    return (
        Object.entries(LOCALE_LANGUAGE_TAGS) as Array<[SdsLocale, string]>
    ).find(([, tag]) => tag.toLowerCase() === normalized)?.[0];
};
